import { Box, ButtonBase } from '@mui/material'

/**
 * A compact, toggleable segmented pill control used for optional single-choice fields
 * (e.g. diagnosis Status, complaint Severity). Clicking the active pill again clears the
 * selection (the field is optional / can be unset). Color-coded per option, wraps on
 * narrow widths and keeps touch-friendly hit areas so it works on mobile.
 *
 * Props:
 *  - options: [{ id, label, color }]
 *  - value:    currently-selected id (or falsy when unset)
 *  - onChange: (idOrNull) => void — receives the option id, or null when toggled off
 *  - disabled: disables all pills
 *  - size:     'small' | 'medium'
 *  - ariaLabel label for the pill group
 */
export default function SegmentedPillSelector({
  options = [],
  value,
  onChange,
  disabled = false,
  size = 'small',
  ariaLabel,
}) {
  const isSmall = size === 'small'

  return (
    <Box
      role="group"
      aria-label={ariaLabel}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.75,
        alignItems: 'center',
      }}
    >
      {(options || []).map((option) => {
        const selected = value === option?.id
        const color = option?.color || '#4338ca'
        return (
          <ButtonBase
            key={option?.id}
            disabled={disabled}
            aria-pressed={selected}
            focusRipple
            onClick={() => {
              if (disabled) {
                return
              }
              onChange?.(selected ? null : option?.id)
            }}
            sx={{
              px: isSmall ? 1.25 : 1.75,
              py: isSmall ? 0.45 : 0.65,
              borderRadius: '999px',
              fontSize: isSmall ? 12 : 13,
              fontWeight: 600,
              lineHeight: 1.4,
              minHeight: isSmall ? 26 : 32,
              whiteSpace: 'nowrap',
              border: `1.5px solid ${selected ? color : `${color}55`}`,
              color: selected ? '#ffffff' : color,
              background: selected ? color : 'transparent',
              boxShadow: selected ? `0 2px 6px ${color}40` : 'none',
              transition: 'all 0.15s ease',
              opacity: disabled ? 0.5 : 1,
              cursor: disabled ? 'not-allowed' : 'pointer',
              '&:hover': disabled
                ? {}
                : {
                    background: selected ? color : `${color}14`,
                    borderColor: color,
                  },
            }}
          >
            {option?.label}
          </ButtonBase>
        )
      })}
    </Box>
  )
}
